import type {
  BusinessRegionsResponse,
  BusinessRevenueResponse,
  FieldIssueNoteDto,
  FieldIssueSummaryResponse,
  SetBusinessRegionInputRequest,
  SetFieldIssueNoteRequest,
  InventoryKanbanResponse,
  ManufacturingCalendarResponse,
  ManufacturingKpiResponse,
  ManufacturingScheduleResponse,
  RemediationStatus,
} from '@bhops/shared';
import { apiClient } from './client';

// docs/plan/team-dashboards-and-production-handoff/plan.md §7.3

// ============================================================
// 계획서 요구 2b(26-08-21) — 설치 현황(국내/해외 카드 + 지역표)의 응답 미러
// ============================================================
//
// **서버 원문(apps/api/src/dashboard/team-dashboard.service.ts InstallOverview)이
// 정본이다** — 칸의 뜻은 그 파일 주석에 있고 여기에는 표기에 필요한 만큼만 적는다
// (customer-master.ts 미러와 같은 규칙 — 두 벌이 되면 갈라진다).

/** 지역표 한 행. key = 국내 시/도('서울') 또는 해외 국가 코드('MN') — overseas 로 가른다. */
export interface InstallRegionRow {
  key: string;
  overseas: boolean;
  /** 이 지역에 **본사를 둔** 설치 보유 고객사 수(본사 주소 기준). */
  customers: number;
  /** 이 지역 **매장에 나가 있는** 로봇 수(매장 주소 기준 — 두 셈의 기준이 다르다). */
  robots: number;
}

export interface InstallOverview {
  /** 국내 설치 대수 — 고객사 국가 KR(마스터 국내/해외 탭과 같은 기준). */
  domesticCount: number;
  /** 해외 설치 대수 — 국가 ≠ KR. */
  overseasCount: number;
  /**
   * 지역표(서버가 대수 내림차순 → 고객사 수 내림차순 → 키로 정렬해 내린다).
   * '(지역 미지정)'·'(미상)' 버킷은 이 배열에 없다(아래 별도 칸 — 화면이 맨 아래 고정 행).
   * robots 0·customers 0 행도 없다. robots 0·customers>0 행은 **있다**(본사만 있는
   * 지역 — 지우면 그 고객사가 표 어디에도 없어진다: 조용한 유실 금지).
   */
  regions: InstallRegionRow[];
  /**
   * 마감 차수(26-08-24) — '(지역 미지정)' 버킷의 **키 글자**. 화면은 이 값을 버킷 행 라벨과
   * 마스터 이동 링크(`?region=…`)에 그대로 쓴다.
   *
   * 종전엔 콘솔이 같은 글자를 사본 상수(`api/customer-master` REGION_UNASSIGNED)로 들고
   * 있었고 그 미러에 게이트가 없었다 — 서버 정본 글자를 다듬으면 api 시험은 전부 초록인데
   * 지역 클릭이 조용히 빈 목록이 됐다. 사본을 지우고 **서버가 준 글자만** 쓴다.
   *
   * 옵셔널인 이유: 이 칸을 아직 안 내리는 서버가 있다(installOverview 는 내리면서 키만
   * 없는 배포 차수). 그때 화면은 버킷 행의 **수는 그대로 보여 주고**(유실 금지) 라벨 자리에
   * 이름을 받지 못했다고 적으며 링크를 걸지 않는다 — 글자를 지어내면 그 링크가 빈 목록으로
   * 간다('(미상)' 행이 이동을 안 하는 것과 같은 규칙: 없는 목적지로 보내지 않는다).
   */
  unassignedRegionKey?: string;
  /** '(지역 미지정)' 행의 대수 — 매장 사슬·본사 폴백까지 전부 비어 시/도를 못 뽑은 로봇. */
  unassignedRegionRobots: number;
  /** '(지역 미지정)' 행의 고객사 수(본사 주소가 비어 있는 설치 보유 고객사). */
  unassignedRegionCustomers: number;
  /**
   * '(미상)' 행의 대수 — 고객사 연결이 없는 배치. 국내/해외 카드 **어느 쪽에도 없다**
   * (국가를 모르는데 어느 쪽이라 말하면 지어내는 것 — 서버 주석).
   * 불변식: domesticCount + overseasCount = Σ regions.robots + unassignedRegionRobots.
   */
  unknownCustomerRobots: number;
}

// S1 Phase 10 (R2 #8) — 사업 대시보드 슬림화. 3 카드만:
// ① 이번 달 신규 계약 ② 마감 임박 계약 ③ 활성 계약 핸드오프 단계.
export interface BusinessDashboard {
  newContractsThisMonth: number;
  expiringContracts: Array<{
    id: string;
    humanId: string;
    // FB2 §3A — 고객사 상세 직링크용.
    customerId: string;
    customerName: string;
    endDate: string;
    daysRemaining: number;
    window: 'D0' | 'D30' | 'D90';
  }>;
  // 핸드오프 단계: 설치 대기 / 설치 완료 / 회수 예정 (사인 대기 제거).
  handoffStages: {
    installReady: number;
    installDone: number;
    returnPlanned: number;
  };
  /**
   * 계획서 요구 2b(26-08-21) — 설치 현황. **구버전 서버는 이 칸 자체가 없다** →
   * 옵셔널로 읽고, 없으면 섹션을 그리지 않고 그 사실을 한 줄로 말한다
   * (조용한 빈칸 금지 — customer-master returnInfo 와 같은 관례).
   */
  installOverview?: InstallOverview;
  /**
   * 계획서 선택 B(26-08-21) → 마감 차수(26-08-24) — '기존고객사' 카드의 두 수.
   *
   * 판정은 마스터 former 필터와 **같은 함수 한 벌**(서버 isFormerCustomer)이지만 **모수는
   * 나라로 갈린다** — 목적지 `/business/master?former=1` 은 라우터가 scope="domestic" 을
   * 주입해 서버가 countryCode:'KR' 을 총계 앞에 걸기 때문. 종전 한 칸(`formerCustomerCount`,
   * 국가 조건 없음)은 해외 고객사가 처음 기존고객사가 되는 날 "카드 7 vs 목록 6" 이 벌어질
   * 약속이었다. 그래서 서버가 국내·해외를 각각 내리고:
   *   · Domestic — 카드의 큰 숫자. 국내 목록(`?former=1`)의 총계와 일치한다.
   *   · Overseas — **0 이 아닐 때만** 카드가 부제로 덧붙이고 그 부제는 해외 탭 + former 로
   *     간다(갈 곳이 있어야 한다). 0 이면 아무 것도 안 붙는다 — 소음 없음.
   * 구버전 서버는 내리지 않는다 → 옵셔널(installOverview 와 같은 배포 차수).
   */
  formerCustomerCountDomestic?: number;
  formerCustomerCountOverseas?: number;
}

export interface ManufacturingDashboard {
  robotsByStatus: Array<{ status: string; count: number }>;
  productionByStatus: Array<{
    status: string;
    planCount: number;
    plannedRobotCount: number;
  }>;
  pendingAssignmentCount: number;
  plansThisWeek: Array<{
    id: string;
    humanId: string;
    plannedCount: number;
    estimatedCompletionAt: string;
    progressPercent: number;
    model: { code: string; displayName: string };
  }>;
  plansThisMonthCount: number;
  delayedPlans: Array<{
    id: string;
    humanId: string;
    estimatedCompletionAt: string;
    plannedCount: number;
    model: { code: string };
  }>;
}

export interface FieldDashboard {
  openServiceOrders: {
    bySeverity: Array<{ severity: string; count: number }>;
  };
  slaRiskNext24hCount: number;
  /** '리포트 · 조치 이력'의 고객사 리포트 중 대기 · 진행중(2026-10-27 — 종전엔 옛 콘솔 리포트를 셌다). */
  openReportsCount: number;
  openReportsBySeverity: Array<{ severity: 'P1' | 'P2' | 'P3'; count: number }>;
  /** 같은 화면의 고장 조치 이력 상태별 건수. */
  remediationStatusCounts: Record<RemediationStatus, number>;
  upcomingInstalls: Array<{
    id: string;
    humanId: string;
    scheduledInstallAt: string;
    handoffStatus: string;
    // FB2 §3A — 고객사 상세 직링크용.
    customerId: string;
    customer: { id: string; displayName: string };
    store: { name: string } | null;
  }>;
  activeDeploymentsCount: number;
}

export interface InstallCalendarItem {
  id: string;
  humanId: string;
  scheduledInstallAt: string;
  installedAt: string | null;
  handoffStatus: string;
  customer: { id: string; displayName: string };
  store: { id: string; name: string } | null;
  lines: Array<{ quantity: number; model: { code: string; displayName: string } }>;
  reservedRobots: Array<{ id: string; serialShort: string | null; serialLong: string | null }>;
  totalRobots: number;
}

export interface InstallCalendarResponse {
  from: string;
  to: string;
  items: InstallCalendarItem[];
}

export const teamDashboardApi = {
  // 2026-10-26 — 권역별 현황(엑셀 '권역별현황'). month 'YYYY-MM', 없으면 이번 달.
  async businessRegions(month?: string): Promise<BusinessRegionsResponse> {
    const { data } = await apiClient.get<BusinessRegionsResponse>('/dashboard/business-regions', {
      params: month ? { month } : {},
    });
    return data;
  },
  async setBusinessRegionInput(body: SetBusinessRegionInputRequest): Promise<void> {
    await apiClient.put('/dashboard/business-regions/inputs', body);
  },
  async business(): Promise<BusinessDashboard> {
    const { data } = await apiClient.get<BusinessDashboard>('/dashboard/business');
    return data;
  },
  // round10 B3 — 월별 계약건수 + 구독/판매 수익. 금액은 서버가 price-visible 역할만 채운다.
  async businessRevenue(monthsBack?: number): Promise<BusinessRevenueResponse> {
    const params = new URLSearchParams();
    if (monthsBack != null) params.set('monthsBack', String(monthsBack));
    const qs = params.toString();
    const { data } = await apiClient.get<BusinessRevenueResponse>(
      `/dashboard/business-revenue${qs ? `?${qs}` : ''}`,
    );
    return data;
  },
  async manufacturing(): Promise<ManufacturingDashboard> {
    const { data } = await apiClient.get<ManufacturingDashboard>('/dashboard/manufacturing');
    return data;
  },
  async field(): Promise<FieldDashboard> {
    const { data } = await apiClient.get<FieldDashboard>('/dashboard/field');
    return data;
  },
  /** 보름 이슈 정리(2026-10-27). period = YYYY-MM-1|2, 없으면 서버가 지난 보름을 고른다. */
  async fieldIssueSummary(period?: string): Promise<FieldIssueSummaryResponse> {
    const { data } = await apiClient.get<FieldIssueSummaryResponse>(
      '/dashboard/field/issue-summary',
      {
        params: period ? { period } : {},
      },
    );
    return data;
  },
  /** 신규 · 누적 이슈 글 고치기(2026-10-28). body 가 비면 자동 글로 돌아간다(null). */
  async setFieldIssueNote(body: SetFieldIssueNoteRequest): Promise<FieldIssueNoteDto | null> {
    const { data } = await apiClient.put<FieldIssueNoteDto | null>(
      '/dashboard/field/issue-summary/note',
      body,
    );
    return data;
  },
  async installCalendar(fromDate?: string, toDate?: string): Promise<InstallCalendarResponse> {
    const params = new URLSearchParams();
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    const { data } = await apiClient.get<InstallCalendarResponse>(
      `/dashboard/field/install-calendar?${params.toString()}`,
    );
    return data;
  },

  // S2 Phase 2.10 — 재고 Kanban (시트4/5).
  async inventoryKanban(): Promise<InventoryKanbanResponse> {
    const { data } = await apiClient.get<InventoryKanbanResponse>('/dashboard/inventory-kanban');
    return data;
  },

  // S2 Phase 2.10 — 제조 대시보드 KPI (시트1): 월별 생산/출고 + 모델별 비중.
  async manufacturingKpi(months?: number): Promise<ManufacturingKpiResponse> {
    const params = new URLSearchParams();
    if (months != null) params.set('months', String(months));
    const qs = params.toString();
    const { data } = await apiClient.get<ManufacturingKpiResponse>(
      `/dashboard/manufacturing-kpi${qs ? `?${qs}` : ''}`,
    );
    return data;
  },

  // S2 Phase 2.10 — 제조 일정 데이터 (S3 Phase 3.8 캘린더 소스).
  async manufacturingSchedule(
    fromDate?: string,
    toDate?: string,
  ): Promise<ManufacturingScheduleResponse> {
    const params = new URLSearchParams();
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    const qs = params.toString();
    const { data } = await apiClient.get<ManufacturingScheduleResponse>(
      `/dashboard/manufacturing-schedule${qs ? `?${qs}` : ''}`,
    );
    return data;
  },

  // S3 Phase 3.8 (spec §3.9) — 제조 대시보드 전용 캘린더 이벤트
  // (제조 계획 / 출고 / 로봇 교체 / 회수). ManufacturingCalendar 가 소비.
  async manufacturingCalendar(
    fromDate?: string,
    toDate?: string,
  ): Promise<ManufacturingCalendarResponse> {
    const params = new URLSearchParams();
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    const qs = params.toString();
    const { data } = await apiClient.get<ManufacturingCalendarResponse>(
      `/dashboard/manufacturing-calendar${qs ? `?${qs}` : ''}`,
    );
    return data;
  },
};
